<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
          {{ tt('Functions', 'Funkcije') }}
        </h2>
        <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {{
            tt(
              'Pre-built automation patterns for common tasks',
              'Vnaprej pripravljeni vzorci avtomatizacije za pogoste naloge',
            )
          }}
        </p>
      </div>
      <button
        @click="showAddModal = true"
        class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2">
        <span class="material-symbols-outlined text-sm">add</span>
        {{ tt('Create function', 'Ustvari funkcijo') }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Empty State -->
    <div v-else-if="automations.length === 0" class="text-center py-12">
      <span class="material-symbols-outlined text-6xl text-gray-400">settings_suggest</span>
      <h3 class="text-lg font-medium text-gray-900 dark:text-white mt-4">
        {{ tt('No device functions yet', 'Ni še funkcij naprav') }}
      </h3>
      <p class="text-gray-600 dark:text-gray-400 mt-2">
        {{
          tt(
            'Create patterns like motion-activated switches or threshold alarms',
            'Ustvarite vzorce, kot so stikala ob gibu ali alarmi ob preseženi meji',
          )
        }}
      </p>
    </div>

    <!-- Automations List -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div
        v-for="automation in automations"
        :key="automation.id"
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
        @click="editAutomation(automation)">
        <!-- Header -->
        <div class="flex items-start justify-between mb-3">
          <div class="flex-1">
            <h3 class="font-semibold text-gray-900 dark:text-white">{{ automation.name }}</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ getTypeLabel(automation.automationType) }}</p>
          </div>
          <button
            @click.stop="toggleEnabled(automation)"
            class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            :class="automation.enabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'">
            <span
              class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              :class="automation.enabled ? 'translate-x-5' : 'translate-x-0'" />
          </button>
        </div>

        <!-- Bindings -->
        <div class="space-y-1 text-sm">
          <div class="text-gray-700 dark:text-gray-300">
            <span class="font-medium">{{ automation.binding_count || 0 }}</span>
            {{ bindingCountLabel(automation.binding_count || 0) }}
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            @click.stop="editAutomation(automation)"
            class="flex-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors">
            {{ tt('Edit', 'Uredi') }}
          </button>
          <button
            @click.stop="deleteAutomation(automation)"
            class="px-3 py-1.5 text-sm bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded transition-colors">
            {{ tt('Delete', 'Izbriši') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <AddDeviceAutomationModal
      v-if="showAddModal"
      :device-id="props.deviceId"
      :device-type="props.deviceType"
      @close="showAddModal = false"
      @saved="handleAutomationSaved" />

    <EditDeviceAutomationModal
      v-if="showEditModal && selectedAutomation"
      :automation="selectedAutomation"
      @close="showEditModal = false"
      @saved="handleAutomationSaved"
      @deleted="handleAutomationDeleted" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { apiFetch } from '../services/api';
import AddDeviceAutomationModal from './AddDeviceAutomationModal.vue';
import EditDeviceAutomationModal from './EditDeviceAutomationModal.vue';
import { useI18n } from 'vue-i18n';
import { useMessageBox } from '../composables/useMessageBox';
import { useDeviceAutomationsStore } from '../stores/deviceAutomations';
import { tt } from '../utils/consoleText';
import type { Bilingual } from '../config/bilingual';

const { t } = useI18n();
const { confirm: confirmBox } = useMessageBox();
const deviceAutomationsStore = useDeviceAutomationsStore();

const props = defineProps<{
  deviceId: string;
  deviceType: string;
}>();

interface DeviceAutomation {
  id: string;
  name: string;
  automationType: string;
  enabled: boolean;
  config: Record<string, unknown>;
  binding_count: number;
  deviceId: string;
}

const automations = ref<DeviceAutomation[]>([]);
const loading = ref(false);
const showAddModal = ref(false);
const showEditModal = ref(false);
const selectedAutomation = ref<DeviceAutomation | null>(null);

/**
 * The automation type's own name, in both languages. A data table cannot know
 * the reader's language, so each type carries both halves and `getTypeLabel`
 * picks one through `tt()` — the shape the binding-role tables below use, and
 * the one `useAutomationBadge`'s `formatAutomationTypeLabel` renders elsewhere.
 */
const typeLabels: Record<string, Bilingual> = {
  light_motion_switch: { en: 'Light Motion Switch', sl: 'Stikalo luči ob gibu' },
  light_brightness_switch: { en: 'Light Brightness Switch', sl: 'Stikalo luči po svetlosti' },
  light_external_switch: { en: 'Light External Switch', sl: 'Zunanje stikalo luči' },
  analog_threshold_alarm: { en: 'Analog Threshold Alarm', sl: 'Analogni alarm ob meji' },
  shade_wind_protection: { en: 'Shade Wind Protection', sl: 'Zaščita senčil pred vetrom' },
  shade_rain_protection: { en: 'Shade Rain Protection', sl: 'Zaščita senčil pred dežjem' },
  shade_sun_tracking: { en: 'Shade Sun Tracking', sl: 'Sončno vodenje senčil' },
  cover_external_control: { en: 'Cover External Control', sl: 'Zunanje krmiljenje senčil' },
  irrigation_controller: { en: 'Irrigation Controller', sl: 'Krmilnik namakanja' },
  irrigation_rain_skip: { en: 'Irrigation Rain Skip', sl: 'Preskok namakanja ob dežju' },
  binary_sensor_alarm: { en: 'Binary Sensor Alarm', sl: 'Alarm binarnega senzorja' },
  switch_off_timer: { en: 'Switch Off Timer', sl: 'Časovnik izklopa' },
  time_switch: { en: 'Time Switch', sl: 'Časovno stikalo' },
  fan_temperature_controller: {
    en: 'Fan Temperature / Humidity Controller',
    sl: 'Krmilnik ventilatorja po temperaturi / vlagi',
  },
  fan_presence_timer: { en: 'Fan Presence Timer', sl: 'Časovnik ventilatorja po prisotnosti' },
  fan_external_control: { en: 'Fan External Control', sl: 'Zunanje krmiljenje ventilatorja' },
  lock_webhook_trigger: { en: 'Lock Webhook Trigger', sl: 'Sprožilec ključavnice prek webhooka' },
  appliance_run_limit: { en: 'Appliance Run Limit', sl: 'Omejitev delovanja aparata' },
  appliance_cycle_finished: { en: 'Appliance Cycle Finished', sl: 'Konec cikla aparata' },
  robot_job_schedule: { en: 'Robot Job Schedule', sl: 'Urnik robota' },
  robot_rain_return: { en: 'Robot Rain Return', sl: 'Vrnitev robota ob dežju' },
  network_device_offline: { en: 'Network Device Offline', sl: 'Naprava brez povezave' },
  // Legacy types
  motion_switch: { en: 'Motion-Activated Switch', sl: 'Stikalo ob gibu' },
  threshold_alarm: { en: 'Threshold Alarm', sl: 'Alarm ob meji' },
  brightness_switch: { en: 'Brightness Switch', sl: 'Stikalo po svetlosti' },
  external_switch: { en: 'External Switch', sl: 'Zunanje stikalo' },
};

const getTypeLabel = (type: string): string => {
  const label = typeLabels[type];
  return label ? tt(label.en, label.sl) : type;
};

/**
 * `N device binding(s)` — the words beside the count this row paints. Slovene
 * needs the count's own form (`2 povezavi`, `3 povezave`, `5 povezav`), so the
 * English half keeps the frame the line has always painted and the Slovene half
 * picks the ending. Same shape as the scene card's action count.
 */
function bindingCountLabel(count: number): string {
  return count === 1
    ? tt('device binding', 'povezava naprave')
    : tt(
        'device bindings',
        slCount(count, 'povezava naprave', 'povezavi naprave', 'povezave naprave', 'povezav naprave'),
      );
}

/** The Slovene count form (`1 povezava`, `2 povezavi`, `3 povezave`, `5 povezav`). */
function slCount(n: number, one: string, two: string, few: string, many: string): string {
  const lastTwo = n % 100;
  if (lastTwo === 1) return one;
  if (lastTwo === 2) return two;
  if (lastTwo === 3 || lastTwo === 4) return few;
  return many;
}

const fetchAutomations = async () => {
  try {
    loading.value = true;
    automations.value = await apiFetch(`/device-automations?deviceId=${props.deviceId}`);
  } catch (err) {
    console.error('Failed to load device automations', err);
  } finally {
    loading.value = false;
  }
};

const toggleEnabled = async (automation: DeviceAutomation) => {
  try {
    await apiFetch(`/device-automations/${automation.id}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled: !automation.enabled }),
    });
    automation.enabled = !automation.enabled;
    deviceAutomationsStore.notifyAutomationsMutated();
  } catch (err) {
    console.error('Failed to toggle automation', err);
  }
};

const editAutomation = (automation: DeviceAutomation) => {
  selectedAutomation.value = automation;
  showEditModal.value = true;
};

const deleteAutomation = async (automation: DeviceAutomation) => {
  const ok = await confirmBox(t('dialogs.automationDeleteConfirm', { name: automation.name }));
  if (!ok) {
    return;
  }

  try {
    await apiFetch(`/device-automations/${automation.id}`, {
      method: 'DELETE',
    });
    automations.value = automations.value.filter((a) => a.id !== automation.id);
    deviceAutomationsStore.notifyAutomationsMutated();
  } catch (err) {
    console.error('Failed to delete automation', err);
  }
};

const handleAutomationSaved = () => {
  showAddModal.value = false;
  showEditModal.value = false;
  fetchAutomations();
};

const handleAutomationDeleted = () => {
  showEditModal.value = false;
  fetchAutomations();
};

onMounted(() => {
  fetchAutomations();
});

// The settings modal / editor can be REUSED across devices (the :device prop
// changes without the component being remounted). Without this, the tab keeps
// showing the previous device's automations while the modal header already
// shows the new device — e.g. opening the valve's form first, then Tenda's,
// left Tenda's form displaying "Terasa zalivanje časovnik".
watch(
  () => props.deviceId,
  () => {
    selectedAutomation.value = null;
    showAddModal.value = false;
    showEditModal.value = false;
    fetchAutomations();
  },
);
</script>
